<script setup>
import { usePaginacionConsumoMPStore } from "@/store/paginacionStore";
import ColumnFilterMenu from "@/components/Shared/ColumnFilterMenu.vue";

const paginacionStore = usePaginacionConsumoMPStore();

defineProps({
  paginados: { type: Array, required: true },
  loading: { type: Boolean, required: true },
  paginaActual: { type: Number, required: true },
  totalPaginas: { type: Number, required: true },
  paginasVisibles: { type: Array, required: true },
  rangoInicio: { type: Number, required: true },
  rangoFin: { type: Number, required: true },
  totalRegistros: { type: Number, required: true },
  items: { type: Array, default: () => [] },
  sortKey: { type: String, required: true },
  sortDir: { type: String, required: true },
  filtroMaquinaSet: { type: Set, default: () => new Set() },
  filtroAditivoSet: { type: Set, default: () => new Set() },
});

const emit = defineEmits([
  "ver-detalle",
  "sort",
  "cambiar-pagina",
  "cambiar-por-pagina",
  "update:filtroMaquinaSet",
  "update:filtroAditivoSet",
]);

function formatFecha(value) {
  if (!value) return "—";
  const [year, month, day] = value.split("-");
  return `${day}/${month}/${year}`;
}
</script>

<template>
  <div class="divisan-table-container shadow-lg d-none d-xl-block">
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-verde"></div>
    </div>
    <table v-else class="table table-divisan align-middle mb-0">
      <thead>
        <tr>
          <th class="ps-4">
            <ColumnFilterMenu
              field="fecha"
              label="Fecha"
              type="date"
              :filterable="false"
              :items="items"
              :sortField="sortKey"
              :sortAsc="sortDir === 'asc'"
              @update:sort="emit('sort', $event)"
            />
          </th>
          <th>
            <ColumnFilterMenu
              field="operador_nombre"
              label="Operador"
              :filterable="false"
              :items="items"
              :sortField="sortKey"
              :sortAsc="sortDir === 'asc'"
              @update:sort="emit('sort', $event)"
            />
          </th>
          <th>
            <ColumnFilterMenu
              field="maquina_nombre"
              label="Máquina"
              :items="items"
              :sortField="sortKey"
              :sortAsc="sortDir === 'asc'"
              :modelValue="filtroMaquinaSet"
              @update:modelValue="emit('update:filtroMaquinaSet', $event)"
              @update:sort="emit('sort', $event)"
            />
          </th>
          <th>
            <ColumnFilterMenu
              field="producto_nombre"
              label="Producto"
              :filterable="false"
              :items="items"
              :sortField="sortKey"
              :sortAsc="sortDir === 'asc'"
              @update:sort="emit('sort', $event)"
            />
          </th>
          <th>
            <ColumnFilterMenu
              field="color_nombre"
              label="Color"
              :filterable="false"
              :items="items"
              :sortField="sortKey"
              :sortAsc="sortDir === 'asc'"
              @update:sort="emit('sort', $event)"
            />
          </th>
          <th class="text-center">
            <ColumnFilterMenu
              field="aditivo_label"
              label="Aditivo"
              :sortable="false"
              :items="items"
              :modelValue="filtroAditivoSet"
              @update:modelValue="emit('update:filtroAditivoSet', $event)"
            />
          </th>
          <th class="text-center">Detalle</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in paginados" :key="item.id">
          <td class="ps-4 text-cafe fw-bold">{{ formatFecha(item.fecha) }}</td>
          <td>
            <i class="bi bi-person-fill me-1 text-verde"></i>{{ item.operador_nombre }}
          </td>
          <td>{{ item.maquina_nombre }}</td>
          <td>{{ item.producto_nombre }}</td>
          <td>
            <span class="badge-color" :title="item.color_nombre">{{
              item.color_nombre
            }}</span>
          </td>
          <td class="text-center">
            <span :class="item.con_aditivo ? 'badge-si' : 'badge-no'">
              {{ item.con_aditivo ? "Sí" : "No" }}
            </span>
          </td>
          <td class="text-center">
            <button
              class="btn-circular"
              @click="emit('ver-detalle', item)"
              title="Ver detalle"
            >
              <i class="bi bi-eye"></i>
            </button>
          </td>
        </tr>
        <tr v-if="paginados.length === 0 && !loading">
          <td colspan="8" class="text-center py-5 text-cafe opacity-50 fw-bold">
            No se encontraron registros
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginación escritorio -->
    <div class="paginacion-wrapper">
      <div class="pag-izq">
        <small class="pag-info"
          >{{ rangoInicio }}–{{ rangoFin }} de {{ totalRegistros }} registros</small
        >
        <div class="por-pagina-wrap">
          <label class="por-pagina-label">Filas:</label>
          <select
            class="por-pagina-select"
            :value="paginacionStore.porPagina"
            @change="emit('cambiar-por-pagina', Number($event.target.value))"
          >
            <option v-for="n in paginacionStore.opcionesPorPagina" :key="n" :value="n">
              {{ n }}
            </option>
          </select>
        </div>
      </div>
      <div class="pag-der">
        <button
          class="btn-pag"
          :disabled="paginaActual === 1"
          @click="emit('cambiar-pagina', 1)"
        >
          <i class="bi bi-chevron-double-left"></i>
        </button>
        <button
          class="btn-pag"
          :disabled="paginaActual === 1"
          @click="emit('cambiar-pagina', paginaActual - 1)"
        >
          <i class="bi bi-chevron-left"></i>
        </button>
        <template v-for="p in paginasVisibles" :key="p">
          <span v-if="p === '...'" class="pag-ellipsis">…</span>
          <button
            v-else
            :class="['btn-pag-num', { activo: p === paginaActual }]"
            @click="emit('cambiar-pagina', p)"
          >
            {{ p }}
          </button>
        </template>
        <button
          class="btn-pag"
          :disabled="paginaActual === totalPaginas"
          @click="emit('cambiar-pagina', paginaActual + 1)"
        >
          <i class="bi bi-chevron-right"></i>
        </button>
        <button
          class="btn-pag"
          :disabled="paginaActual === totalPaginas"
          @click="emit('cambiar-pagina', totalPaginas)"
        >
          <i class="bi bi-chevron-double-right"></i>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}

.divisan-table-container {
  background-color: var(--d-cream);
  border-radius: 15px;
  overflow: hidden;
}
.table-divisan thead th {
  background: var(--d-cream);
  color: var(--d-brown);
  border-bottom: 3px solid var(--d-brown);
}
.table-divisan th {
  padding: 15px;
  font-weight: 800;
  text-transform: capitalize;
  border: none;
  white-space: nowrap;
}
.table-divisan td {
  border-bottom: 1px solid rgba(96, 108, 56, 0.1);
  padding: 12px 15px;
}
.table-divisan tbody tr:hover td {
  background-color: rgba(96, 108, 56, 0.05);
}
.btn-circular {
  background: white;
  border: 1px solid var(--d-green);
  border-radius: 50%;
  width: 35px;
  height: 35px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--d-green);
  transition: 0.2s;
  cursor: pointer;
}
.btn-circular:hover {
  background-color: var(--d-green);
  color: var(--d-cream);
}

.badge-color {
  display: inline-block;
  background: rgba(96, 108, 56, 0.12);
  color: var(--d-brown);
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 0.8rem;
  font-weight: 600;
}
.badge-si {
  display: inline-block;
  background: #d4edda;
  color: #155724;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 0.8rem;
  font-weight: 700;
}
.badge-no {
  display: inline-block;
  background: #f8d7da;
  color: #721c24;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 0.8rem;
  font-weight: 700;
}

/* ── Paginación ── */
.paginacion-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  flex-wrap: wrap;
  gap: 8px;
  border-top: 1px solid rgba(64, 34, 8, 0.1);
  background: rgba(96, 108, 56, 0.05);
}
.pag-izq {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.pag-der {
  display: flex;
  align-items: center;
  gap: 4px;
}
.pag-info {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}
.por-pagina-label {
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--d-green);
  white-space: nowrap;
}
.por-pagina-select {
  appearance: none;
  -webkit-appearance: none;
  background: white
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%23606c38' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E")
    no-repeat right 8px center;
  border: 1.5px solid var(--d-green);
  border-radius: 8px;
  padding: 3px 26px 3px 10px;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--d-brown);
  cursor: pointer;
  outline: none;
  min-width: 58px;
}
.btn-pag {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.35);
  color: var(--d-brown);
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.8rem;
}
.btn-pag:hover:not(:disabled) {
  background: var(--d-green);
  color: var(--d-cream);
  border-color: var(--d-green);
}
.btn-pag:disabled {
  opacity: 0.3;
  cursor: default;
}
.btn-pag-num {
  background: white;
  border: 1.5px solid rgba(96, 108, 56, 0.25);
  color: var(--d-brown);
  border-radius: 8px;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.18s;
  font-size: 0.82rem;
  font-weight: 700;
}
.btn-pag-num:hover {
  background: rgba(96, 108, 56, 0.1);
  border-color: var(--d-green);
}
.btn-pag-num.activo {
  background: var(--d-brown);
  color: var(--d-cream);
  border-color: var(--d-brown);
  pointer-events: none;
}
.pag-ellipsis {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 32px;
  font-size: 0.85rem;
  color: var(--d-brown);
  opacity: 0.4;
  font-weight: 700;
}
</style>
